import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, withFormApi } from 'informed'
import moment from 'moment'
import { useTranslation } from 'react-i18next'

import { Radio, DateTimePicker } from 'components/forms'
import Divider from 'components/Divider'
import Button from 'components/Button'
import FlexedDiv from '../StyledComponents/FlexedDiv'
import { StyledDatePickerContainer } from './styled'

const DateIntervalForm = ({ field, formApi, maxDate, disabled = false }) => {
	const { t } = useTranslation()
	const formState = formApi.getState()
	const [, setUpdater] = useState(Math.random())

	const handleChange = ({ target: { value, name } }) => {
		if(value) {
			const from = moment().utc().set({ [name]: value }).startOf(name)
			const to = moment(from).utc().endOf(name)
			formApi.setValues({
				...formState.values,
				[field]: { from, to }
			})
			setUpdater(Math.random()) // Update Radio 'checked' prop
		}
	}

	const [selectedYear, setSelectedYear] = useState(moment().year())
	const [showExtended, setShowExtended] = useState(false)

	const handleChangeSpecificPeriod = (value, period = 'month') => {
		if(value || value === 0) {
			const prevFields = formApi.getValues().period
			let from = moment(prevFields?.from).utc()
			let to = moment(prevFields?.to).utc()

			if(period === 'halfYear' && value === 1) {
				from = from.set({ quarter: 1 }).startOf('quarter')
				to = to.set({ quarter: 2 }).endOf('quarter')
				formApi.setValues({
					...formState.values,
					[field]: { from, to }
				})
			}
			if(period === 'halfYear' && value === 2) {
				from = from.set({ quarter: 3 }).startOf('quarter')
				to = to.set({ quarter: 4 }).endOf('quarter')
				formApi.setValues({
					...formState.values,
					[field]: { from, to }
				})
			}
			if (period === 'year') {
				from = from.set({ [period]: value }).startOf('year')
				to = to.set({ [period]: value }).endOf('year')
				formApi.setValues({
					...formState.values,
					[field]: { from, to }
				})
				setSelectedYear(value)
			} else {
				from = from?.set({ [period]: value }).startOf(period)
				to = to?.set({ [period]: value }).endOf(period)
				formApi.setValues({
					...formState.values,
					[field]: { from, to }
				})
			}
			setUpdater(Math.random()) // Update Radio 'checked' prop
		}
	}

	const getPreviousDate = currentDateType => currentDateType && currentDateType - 1
	const getNextDate = currentDateType => currentDateType && currentDateType + 1

	const checkPeriod = ({ shift = 0, type }) => {
		const { from, to } = formApi.getValue(field) || formApi.getInitialValue(field) || {}
		if(!from || !to) {
			return false
		}
		const fromMoment = moment(from).utc()
		const toMoment = moment(to).utc()
		const diff = toMoment.diff(fromMoment, type, true)
		if(diff > 1) {
			return false
		}
		const compareFrom = moment().utc().subtract(shift, type).startOf(type)
		const compareTo = moment(compareFrom).endOf(type)
		return compareFrom.isSame(fromMoment, 'day') && compareTo.isSame(toMoment, 'day')
	}

	const checkMonth = (value) => {
		const { from, to } = formApi.getValue(field) || formApi.getInitialValue(field) || {}
		if(!from || !to) {
			return false
		}
		const fromMoment = moment(from).utc()
		const toMoment = moment(to).utc()

		const compareFrom = moment(from).set({ month: value }).startOf('month')
		const compareTo = moment(to).set({ month: value }).endOf('month')

		return compareFrom.isSame(fromMoment, 'day') && compareTo.isSame(toMoment, 'day')
	}

	// Date types
	const currentDay = moment().day()
	const currentWeek = moment().week()

	return(
		<Fragment>
			<Radio disabled={disabled} noForm onChange={handleChange} checked={checkPeriod({ type: 'day' })} name='day' value={currentDay} label={t('common.dates.today')} />
			<Radio disabled={disabled} noForm onChange={handleChange} checked={checkPeriod({ shift: 1, type: 'day' })} name='day' value={getPreviousDate(currentDay)} label={t('common.dates.last_day')} />
			<Divider visible width={35} />
			<Radio disabled={disabled} noForm onChange={handleChange} checked={checkPeriod({ type: 'week' })} name='week' value={currentWeek} label={t('common.dates.current_week')} />
			<Radio disabled={disabled} noForm onChange={handleChange} checked={checkPeriod({ shift: 1, type: 'week' })} name='week' value={getPreviousDate(currentWeek)} label={t('common.dates.last_week')} />
			<Divider visible width={35} />

			<Button
				disabled={disabled}
				status='dropdown'
				text={t('credit_client_section.strings.show_more')}
				onClick={() => setShowExtended(prevShowExtended => !prevShowExtended)}
			/>

			{showExtended && (
				<Fragment>
					<FlexedDiv vMargin='20px'>
						<Button
							onClick={() => handleChangeSpecificPeriod(getPreviousDate(selectedYear), 'year')}
							status='cancelation'
							shrink
							text={t('credit_client_section.strings.previous')}
						/>
						<Button
							onClick={() => handleChange({ target: { value: selectedYear, name: 'year' } })}
							status='primary'
							shrink
							text={selectedYear?.toString?.()}
						/>
						<Button
							onClick={() => handleChangeSpecificPeriod(getNextDate(selectedYear), 'year')}
							status='cancelation'
							shrink
							text={t('credit_client_section.strings.next')}
						/>
					</FlexedDiv>

					<FlexedDiv vMargin='20px'>
						<Button
							onClick={() => handleChangeSpecificPeriod(1, 'halfYear')}
							status='primary'
							fWidth
							text={`1 ${t('common.dates.halfAYear')}`}
						/>
						<Button
							onClick={() => handleChangeSpecificPeriod(2, 'halfYear')}
							status='primary'
							fWidth
							wMargin
							text={`2 ${t('common.dates.halfAYear')}`}
						/>
					</FlexedDiv>

					<Divider visible width={35} />

					<FlexedDiv vMargin='20px'>
						<Button
							onClick={() => handleChangeSpecificPeriod(1, 'quarter')}
							status='primary'
							fWidth
							text={`1 ${t('common.dates.quarter')}`}
						/>
						<Button
							onClick={() => handleChangeSpecificPeriod(2, 'quarter')}
							status='primary'
							fWidth
							wMargin
							text={`2 ${t('common.dates.quarter')}`}
						/>
					</FlexedDiv>

					<FlexedDiv vMargin='20px'>
						<Button
							onClick={() => handleChangeSpecificPeriod(3, 'quarter')}
							status='primary'
							fWidth
							text={`3 ${t('common.dates.quarter')}`}
						/>
						<Button
							onClick={() => handleChangeSpecificPeriod(4, 'quarter')}
							status='primary'
							fWidth
							wMargin
							text={`4 ${t('common.dates.quarter')}`}
						/>
					</FlexedDiv>

					<Divider visible width={35} />

					<FlexedDiv vMargin='20px'>
						{getMonthes(t).map((quarter, i) => (
							<FlexedDiv key={i} isVertical>
								{quarter.map(month => (
									<Radio
										key={month.value}
										noForm
										onChange={() => handleChangeSpecificPeriod(month.value)}
										checked={checkMonth(month.value)}
										name='month'
										value={month.value}
										label={month.name}
									/>

								))}
							</FlexedDiv>
						))}
					</FlexedDiv>
				</Fragment>
			)}

			<Divider visible width={35} />

			<Divider />
			<StyledDatePickerContainer disabled={disabled}>
				<DateTimePicker
					disabled={disabled}
					label={t('common.fields.period')}
					maxDate={maxDate}
					field={disabled ? null : field}
					interval={true}
					placeholder={{ from: t('common.placeholders.from_date'), to: t('common.placeholders.to_date') }}
					onChange={() => setUpdater(Math.random())}
					popperModifiers={{
						flip: {
							enabled: false
						}
					}}
				/>
			</StyledDatePickerContainer>
		</Fragment>
	)
}

DateIntervalForm.propTypes = {
	field: PropTypes.string
}

DateIntervalForm.defaultProps = {
	field: 'period'
}

export default withFormApi(DateIntervalForm)

const getMonthes = (t) => ([
	[{
		value: 0,
		name: t('common.dates.january')
	},
	{
		value: 1,
		name: t('common.dates.february')
	},
	{
		value: 2,
		name: t('common.dates.march')
	}],
	[{
		value: 3,
		name: t('common.dates.april')
	},
	{
		value: 4,
		name: t('common.dates.may')
	},
	{
		value: 5,
		name: t('common.dates.june')
	}],
	[{
		value: 6,
		name: t('common.dates.july')
	},
	{
		value: 7,
		name: t('common.dates.august')
	},
	{
		value: 8,
		name: t('common.dates.september')
	}],
	[{
		value: 9,
		name: t('common.dates.october')
	},
	{
		value: 10,
		name: t('common.dates.november')
	},
	{
		value: 11,
		name: t('common.dates.december')
	}]
])
